import { useState, useRef } from 'react'
import {
  Link as RouterLink,
  useNavigate,
  useSearchParams,
} from 'react-router-dom'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import {
  Alert,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Link,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { useGoogleLogin } from '@react-oauth/google'
import { routes } from '@/router'
import { useCreateGoogleSession, useCreateSession } from '@/api/user-manager'
import { getPendingInvite, pendingInvitePath } from '@/helpers'
import { usePlatform } from '@/hooks'
import logoGoogle from '@/assets/images/logo-google.svg'
import { LoginFormData, loginFormSchema } from './form.shema'
import { sessionButtonSx } from '../session-button'
import {
  RedirectIfAuthenticated,
  SessionField,
  SessionLegal,
  SessionScreen,
} from '../session-screen'

export function SignInPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [showPassword, setShowPassword] = useState(false)
  const passwordInputRef = useRef<HTMLInputElement>(null)
  const { platform } = usePlatform()
  const isDesktop = useMediaQuery('(min-width:900px)', { noSsr: true })
  const pendingInvite = getPendingInvite()
  const afterLoginPath = pendingInvite
    ? pendingInvitePath(pendingInvite)
    : routes.home

  const { handleSubmit, control } = useForm<LoginFormData>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: {
      email: searchParams.get('email') ?? '',
      password: '',
    },
  })

  const { mutateAsync: createGoogleSession, isPending: isGooglePending } =
    useCreateGoogleSession({
      onSuccess({ access_token }) {
        localStorage.setItem('access_token', access_token)
        navigate(afterLoginPath)
      },
    })

  const login = useGoogleLogin({
    onSuccess: async (response) => {
      createGoogleSession({ access_token: response.access_token })
    },
  })

  const { mutateAsync: createSession, isPending } = useCreateSession({
    onSuccess({ access_token }) {
      localStorage.setItem('access_token', access_token)
      navigate(afterLoginPath)
    },
  })

  function handleTogglePasswordVisibility() {
    if (!passwordInputRef.current) return

    const currentPosition = passwordInputRef.current.selectionStart || 0
    setShowPassword((current) => !current)

    requestAnimationFrame(() => {
      if (!passwordInputRef.current) return
      passwordInputRef.current.setSelectionRange(
        currentPosition,
        currentPosition,
      )
      passwordInputRef.current.focus()
    })
  }

  return (
    <RedirectIfAuthenticated to={afterLoginPath}>
      <Box
        component="form"
        onSubmit={handleSubmit(({ email, password }) => {
          createSession({
            alias: email.toLowerCase(),
            alias_type: 'email',
            password,
          })
        })}
        sx={{ display: 'flex', flex: 1 }}
      >
        <SessionScreen
          title={isDesktop ? 'Login' : 'Entre na sua conta'}
          canBack={!isDesktop}
          onBack={() => navigate(routes.session.welcome)}
          footer={
            <>
              <LoadingButton
                type="submit"
                size="large"
                fullWidth
                loading={isPending}
                sx={sessionButtonSx()}
              >
                Entrar
              </LoadingButton>
              <Button
                variant="outlined"
                size="large"
                fullWidth
                component={RouterLink}
                to={routes.session.signup.name}
                sx={sessionButtonSx()}
              >
                Ainda não sou cliente
              </Button>
              <SessionLegal />
            </>
          }
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {pendingInvite && (
              <Alert severity="info">
                Entre com sua conta para aceitar o convite.
              </Alert>
            )}
            {platform === 'web' && (
              <Button
                variant="outlined"
                size="large"
                fullWidth
                onClick={() => login()}
                disabled={isGooglePending}
                startIcon={
                  <Box
                    component="img"
                    src={logoGoogle}
                    alt=""
                    width={18}
                    height={18}
                  />
                }
                sx={sessionButtonSx()}
              >
                Entrar com Google
              </Button>
            )}

            {platform === 'web' && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  color: 'text.secondary',
                }}
              >
                <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                <Typography variant="body2">ou</Typography>
                <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
              </Box>
            )}

            <Controller
              name="email"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <SessionField
                  {...field}
                  id="signin-email"
                  label="E-mail"
                  type="email"
                  autoComplete="email"
                  placeholder="seu@email.com"
                  errorText={error?.message}
                  onChange={(event) =>
                    field.onChange(event.target.value.toLowerCase())
                  }
                  slotProps={{
                    input: {
                      autoCapitalize: 'none',
                    },
                  }}
                />
              )}
            />

            <Controller
              name="password"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <SessionField
                  {...field}
                  id="signin-password"
                  label="Senha"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  errorText={error?.message}
                  inputRef={passwordInputRef}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={
                              showPassword ? 'Ocultar senha' : 'Mostrar senha'
                            }
                            onClick={handleTogglePasswordVisibility}
                            onMouseDown={(event) => event.preventDefault()}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            />

            <Typography variant="body2" color="text.secondary">
              Esqueceu sua senha?{' '}
              <Link component={RouterLink} to={routes.session.passwordRecovery}>
                Recuperar
              </Link>
            </Typography>
          </Box>
        </SessionScreen>
      </Box>
    </RedirectIfAuthenticated>
  )
}
